/**
 * Which tabs of the Time Tracker a given reader may open, and which one opens.
 *
 * A module of its own, and pure, because this is an access decision. Two of the
 * five tabs used to be rendered unconditionally while the other three were
 * gated, so Team Time and Project Time were offered to everyone — two screens
 * whose only endpoint is `GET /time-entries/team`, which requires
 * `time.reports.team`. Both ways of arriving there end badly. WITHOUT the
 * permission the request is refused, and the tab can only report the 403 as a
 * failed load — a locked door rather than a screen. WITH it, the read is
 * scoped to the projects the caller manages and deliberately leaves the
 * reader's own hours out (HW-870), so whoever manages nothing gets an empty
 * screen. Neither state is worth a tab. [HW-876]
 *
 * Kept out of the component because the repo's vitest runs in a node
 * environment — a component cannot be rendered in a test here, and an access
 * rule nobody can exercise is an access rule that drifts. Same shape as
 * `components/layout/nav-items.ts`, which answers the same question for the
 * sidebar.
 */

/** Every tab id the page knows, in the order the bar shows them. */
export const TIME_TAB_IDS = [
  'dashboard',
  'my-time',
  'team-time',
  'project-time',
  'reports',
] as const;

export type TimeTabId = (typeof TIME_TAB_IDS)[number];

/**
 * Whether a URL's `?tab=` names a tab at all — spelling only, never authority.
 *
 * The route validates the search params before any permission is loaded, so it
 * can say "no such tab" and nothing more; whether the reader may open the tab it
 * named is `resolveTimeTab`'s answer, one layer in.
 */
export function isTimeTabId(value: unknown): value is TimeTabId {
  return typeof value === 'string' && (TIME_TAB_IDS as readonly string[]).includes(value);
}

/** Everything the tab bar needs to decide what a given reader may open. */
export interface TimeTabAccess {
  /**
   * `time.reports.team` — the permission `GET /time-entries/team` and
   * `/time-entries/team-summary` enforce.
   *
   * One code for three tabs because it is one read: Dashboard, Team Time and
   * Project Time are three renderings of the same scoped query, and gating them
   * apart would let the bar advertise a tab whose only endpoint refuses. It also
   * keeps the decision where an administrator can already see it — the
   * "View team time reports" row of Settings → Access Control — instead of in a
   * role name compiled into the bundle.
   */
  canReadTeamTime: boolean;
  /** `time.reports.generate` — what the Reports tab's two surfaces enforce. */
  canGenerateReports: boolean;
}

export interface TimeTab {
  id: TimeTabId;
  label: string;
}

const TAB_LABELS: Record<TimeTabId, string> = {
  dashboard: 'Dashboard',
  'my-time': 'My Time',
  'team-time': 'Team Time',
  'project-time': 'Project Time',
  reports: 'Reports',
};

/**
 * The tabs this reader may open, in the order they are shown.
 *
 * My Time is unconditional: it reads `GET /time-entries` under `time.view`,
 * which the route itself already refuses to render without, so a reader who got
 * this far holds it. For most of the company it is the only entry here, and the
 * page drops the bar rather than draw navigation with one destination.
 */
export function buildTimeTabs(access: TimeTabAccess): TimeTab[] {
  const tab = (id: TimeTabId): TimeTab => ({ id, label: TAB_LABELS[id] });
  return [
    ...(access.canReadTeamTime ? [tab('dashboard')] : []),
    tab('my-time'),
    ...(access.canReadTeamTime ? [tab('team-time'), tab('project-time')] : []),
    ...(access.canGenerateReports ? [tab('reports')] : []),
  ];
}

/**
 * Which tab actually opens, given what the URL asked for.
 *
 * A tab the reader may not open falls back instead of rendering an empty shell.
 * `?tab=project-time` outlives the session that produced it: it sits in
 * bookmarks, and it gets pasted between colleagues whose roles differ — which is
 * the whole point of keeping the tab in the URL. Landing on Dashboard for
 * whoever may read it is the behaviour this page already had.
 */
export function resolveTimeTab(requested: TimeTabId | undefined, access: TimeTabAccess): TimeTabId {
  const allowed = buildTimeTabs(access).map((t) => t.id);
  if (requested && allowed.includes(requested)) return requested;
  return allowed.includes('dashboard') ? 'dashboard' : 'my-time';
}

/**
 * The `view` param that belongs with a tab — which is only ever Reports'.
 *
 * `view` names one of the two Reports surfaces and means nothing anywhere else,
 * but every navigation on this page used to carry it along regardless: clicking
 * My Time from a built report left `?view=builder` in the address, the week
 * stepper spread it through every step, and it rode into any link copied from
 * that state. Not merely untidy — `builderOpened` is seeded from it, so a stray
 * `view=builder` mounts the Builder and fires its queries for someone who never
 * chose it.
 *
 * So every write to this URL goes through here, and the four are worth naming
 * because a rule applied by three of them is not a rule: the tab bar, the week
 * stepper, Today, and the effect that corrects the address on load. The last one
 * is what makes the others safe — it is the only one that can refuse a `view`
 * this page did not write, arriving from a bookmark or a hand-typed link.
 * [HW-876]
 */
export function viewForTab<T>(tab: TimeTabId, view: T): T | undefined {
  return tab === 'reports' ? view : undefined;
}
